import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import './leaderboard.css';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { apiUrl } from '../../utils/apiUrl';

const PUBLIC = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const trophySrc = `${PUBLIC}/img/trophy.png`;
const PAGE_SIZE = 6;

const TOP_TITLES = [
  { text: 'Spotlight Stealer', rank: 1 },
  { text: 'Scene Slayer', rank: 2 },
  { text: 'Rising Star', rank: 3 },
];

function parseTimeToSeconds(t) {
  if (t == null || typeof t !== 'string') return 0;
  const parts = t.split(':').map((p) => parseInt(String(p).trim(), 10) || 0);
  if (parts.length >= 2) {
    const [a, b, c] = parts;
    if (parts.length >= 3) return a * 3600 + b * 60 + c;
    return a * 60 + b;
  }
  return 0;
}

function formatScoreDisplay(score) {
  const n = Math.max(0, parseInt(String(score ?? 0), 10) || 0);
  if (n > 999) return String(n);
  return String(n).padStart(3, '0');
}

/**
 * Pick a numeric score for the table. Prefer raw DB columns when `score` is 0
 * (e.g. points NULL but total_score set). Order: points → total_score → spot_score → score.
 */
function pickDisplayScore(m) {
  const keys = ['points', 'total_score', 'spot_score', 'score'];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (!(key in m)) continue;
    const v = m[key];
    if (v == null || v === '') continue;
    const n = parseInt(String(v).trim(), 10);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return 0;
}

function pickDisplayTime(m) {
  const raw = m.time ?? m.reportTime;
  if (raw == null || raw === '') return '00:00';
  const s = String(raw).trim().slice(0, 50);
  return s || '00:00';
}

/** Normalize API rows for display. */
function normalizeLeaderboardMatch(m) {
  return { ...m, score: pickDisplayScore(m), time: pickDisplayTime(m) };
}

function Leaderboard() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);
  const fromMobileAppInUrl = searchParams.get('fromMobileApp') === 'true';

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [feedbackDone, setFeedbackDone] = useState(
    () => !!location.state?.feedbackSubmitted
  );

  useEffect(() => {
    if (!user || !adminToken) return;
    let cancelled = false;

    (async () => {
      try {
        const resp = await fetch(apiUrl('/gameMatchUsers'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json();
        if (!cancelled && data.success) {
          const raw = data.matches || [];
          setMatches(raw.map(normalizeLeaderboardMatch));
        }
      } catch (e) {
        console.error('gameMatchUsers', e);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [user, adminToken]);

  useEffect(() => {
    if (!fromMobileAppInUrl) return;
    const timerId = window.setTimeout(() => {
      window.GameStatus?.postMessage?.('navigate_back');
    }, 5000);

    return () => window.clearTimeout(timerId);
  }, [fromMobileAppInUrl]);

  useEffect(() => {
    if (!user || !adminToken) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl('/spotGameStatus'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const raw = data.userFeedback;
        if (data.success && raw != null && String(raw).trim() !== '') {
          setFeedbackDone(true);
        }
      } catch (e) {
        console.error('spotGameStatus (leaderboard)', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  const btnBg = themeData?.button_backgroundcolor || '#034b82';
  const btnText = themeData?.button_color || '#ffffff';

  const sortedRows = useMemo(() => {
    const copy = [...matches];
    copy.sort((a, b) => {
      const sb = b.score ?? 0;
      const sa = a.score ?? 0;
      if (sb !== sa) return sb - sa;
      return parseTimeToSeconds(a.time) - parseTimeToSeconds(b.time);
    });
    return copy.map((m, i) => ({ ...m, displayRank: i + 1 }));
  }, [matches]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, page]);

  return (
    <div className="lb-page lb-page--stage">
      <div className="lb-inner">
        <header className="lb-hero">
          <div className="lb-title-row">
            <img src={trophySrc} alt="" className="lb-title-trophy" width={48} height={48} />
            <h1 className="lb-heading-main">Leaderboard</h1>
            <img src={trophySrc} alt="" className="lb-title-trophy" width={48} height={48} />
          </div>
          <p className="lb-subtitle-stage">Who stole the spotlight?</p>
        </header>

        <div className="lb-card lb-card--stage">
          {loading ? (
            <div className="lb-loading">
              <div className="lb-spinner" />
              <span>Loading rankings…</span>
            </div>
          ) : matches.length === 0 ? (
            <div className="lb-empty">
              <p>No data available yet.</p>
            </div>
          ) : (
            <>
              <div className="lb-stage-table" role="table" aria-label="Leaderboard rankings">
                <div className="lb-stage-head" role="row">
                  <div className="lb-stage-hcell lb-stage-hcell--rank" role="columnheader">Rank</div>
                  <div className="lb-stage-hcell lb-stage-hcell--name" role="columnheader">Name</div>
                  <div className="lb-stage-hcell lb-stage-hcell--score" role="columnheader">Score</div>
                  <div className="lb-stage-hcell lb-stage-hcell--time" role="columnheader">Time</div>
                </div>
                <div className="lb-stage-body">
                  {pageRows.map((m) => {
                    const title =
                      m.displayRank <= 3
                        ? TOP_TITLES[m.displayRank - 1]
                        : null;
                    return (
                      <div
                        key={m.userId || `${m.name}-${m.displayRank}`}
                        role="row"
                        className={`lb-stage-row${m.isSelf ? ' lb-stage-row--self' : ''}`}
                      >
                        <div className="lb-stage-cell lb-stage-cell--rank" role="cell">{m.displayRank}</div>
                        <div className="lb-stage-cell lb-stage-cell--name" role="cell">
                          <span className="lb-name-line">
                            {m.name}
                            {m.isSelf ? ' (You)' : ''}
                            {title && (
                              <span
                                className={`lb-name-title lb-name-title--r${title.rank}`}
                              >
                                {' '}
                                ({title.text})
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="lb-stage-cell lb-stage-cell--score" role="cell">{formatScoreDisplay(m.score)}</div>
                        <div className="lb-stage-cell lb-stage-cell--time" role="cell">{m.time || '00:00'}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {totalPages > 1 ? (
                <nav className="lb-pagination" aria-label="Leaderboard pages">
                  <button
                    type="button"
                    className="lb-page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="lb-page-info">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="lb-page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </nav>
              ) : null}
            </>
          )}
        </div>

        <div className="lb-feedback-wrap">
          {feedbackDone ? (
            <div
              className="lb-feedback-done"
              style={{
                backgroundColor: btnBg,
                color: btnText,
              }}
            >
              Thanks — you’ve submitted feedback for this session.
            </div>
          ) : (
            <Link
              to="/feedback"
              replace
              className="lb-feedback-link"
              style={{
                backgroundColor: btnBg,
                color: btnText,
              }}
            >
              Rate your experience
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
